import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Badge, type BadgeTone, Button, GhostButton, vars } from '@agendor/ui';
import { Select, WarningBanner } from '../components/Input';
import { Modal } from '../components/Modal';
import BusinessService, {
    BusinessDetails,
    TemplateMappingAction,
    TemplateMappingsResponse,
    TemplateMappingsAvailableTemplate,
} from '../business-service';
import { useSingleModalRoute } from '../navigation';
import { useToast } from '../toast-context';
import { ConfigCard, ConfigInfo, ConfigTitle, ConfigSummary, SummaryItem } from './AssistantSection';

type TemplateMappingsCardProps = {
    business: BusinessDetails;
    service: BusinessService | null;
    isActive: boolean;
    modalSegments: string[];
    onNavigate: (segments: string[], replace?: boolean) => void;
};

const ActionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const ActionRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
`;

const ActionLabel = styled.h4`
    margin: 0;
    font-weight: 600;
    font-size: 1rem;
    color: ${vars.foregroundColor};
    display: inline;
`;

const ActionDescription = styled.p`
    margin: 8px 0 0;
    font-size: 0.85rem;
    color: #666;
`;

const ValidationError = styled.div`
    font-size: 0.85rem;
    color: #991b1b;
    background-color: #fef2f2;
    border-radius: 6px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const ValidationErrorTitle = styled.span`
    font-weight: 600;
`;

const MissingParamRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 6px;
    font-size: 0.8rem;
`;

const MissingParamName = styled.code`
    font-family: monospace;
    font-weight: 600;
`;

const MissingParamExample = styled.span`
    color: #6b7280;
`;

const TemplatePreview = styled.div`
    font-size: 0.8rem;
    color: #666;
    background-color: ${vars.backgroundColorBright};
    border-radius: 6px;
    padding: 10px 12px;
    white-space: pre-wrap;
    line-height: 1.4;
`;

const ExternalLink = styled(GhostButton).attrs({ hoverBg: 'bright', hoverBorder: false })`
    font-size: 0.85rem;
    text-align: center;
`;

const InactiveSection = styled.details``;

const InactiveSummary = styled.summary`
    padding: 4px 0;
    font-size: 0.9rem;
    color: ${vars.foregroundColor};
    cursor: pointer;
    list-style: none;
    display: flex;
    align-items: center;
    gap: 8px;

    &::-webkit-details-marker {
        display: none;
    }

    &::before {
        content: '▶';
        font-size: 0.6rem;
        transition: transform 0.15s;
    }

    details[open] > &::before {
        transform: rotate(90deg);
    }
`;

const InactiveContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 16px;
`;

function statusTone(status: string, isValid: boolean): BadgeTone {
    if (status === 'NOT_FOUND') return 'danger';
    if (status !== 'APPROVED') return 'warning';
    if (!isValid) return 'danger';
    return 'success';
}

function statusLabel(status: string): string {
    switch (status) {
        case 'APPROVED':
            return 'Aprovado';
        case 'PENDING':
            return 'Pendente';
        case 'REJECTED':
            return 'Rejeitado';
        case 'NOT_FOUND':
            return 'Não encontrado';
        default:
            return status;
    }
}

const INTAKE_ACTIONS = new Set(['new_booking_intake', 'intake_reminder']);
const PAYMENT_ACTIONS = new Set(['payment_confirmed', 'booking_cancelled_no_payment']);
const SUBSCRIPTION_ACTIONS = new Set([
    'client_subscription_invoice',
    'client_subscription_activated',
    'client_subscription_cancelled',
    'client_subscription_charge_failed',
]);

function isActionActive(action: string, business: BusinessDetails): boolean {
    if (action === 'booking_confirmation') return business.bookingConfirmation !== null;
    if (action === 'booking_reminder')
        return business.bookingReminders.enabled && business.bookingReminders.items.length > 0;
    if (INTAKE_ACTIONS.has(action)) return (business.intakes?.length ?? 0) > 0;
    if (PAYMENT_ACTIONS.has(action)) return business.bookingPayment != null;
    if (SUBSCRIPTION_ACTIONS.has(action)) return business.clientSubscriptionsEnabled === true;
    return true;
}

function ActionRowContent({
    action,
    draft,
    availableTemplates,
    saving,
    onSelectChange,
}: {
    action: TemplateMappingAction;
    draft: Map<string, string>;
    availableTemplates: TemplateMappingsAvailableTemplate[];
    saving: boolean;
    onSelectChange: (action: string, templateName: string) => void;
}) {
    const currentTemplateName = draft.get(action.action) ?? action.templateName;
    const waTemplate = availableTemplates.find((t) => t.name === currentTemplateName);
    const missingParams = waTemplate
        ? action.requiredParameters.filter((p) => !waTemplate.parameters.includes(p))
        : action.requiredParameters;
    const tone = statusTone(waTemplate?.status ?? 'NOT_FOUND', missingParams.length === 0);

    return (
        <ActionRow>
            <div>
                <ActionLabel>{action.label}</ActionLabel>{' '}
                <Badge tone={tone}>{statusLabel(waTemplate?.status ?? 'NOT_FOUND')}</Badge>
                <ActionDescription>{action.description}</ActionDescription>
            </div>
            <Select
                value={currentTemplateName}
                onChange={(e) => onSelectChange(action.action, e.target.value)}
                disabled={saving}
            >
                {availableTemplates.map((t) => (
                    <option key={t.name} value={t.name}>
                        {t.name}
                        {t.status !== 'APPROVED' ? ` (${statusLabel(t.status)})` : ''}
                    </option>
                ))}
            </Select>
            {waTemplate && <TemplatePreview>{waTemplate.text}</TemplatePreview>}
            {waTemplate && missingParams.length > 0 && (
                <ValidationError>
                    <ValidationErrorTitle>Parâmetros faltando no template selecionado:</ValidationErrorTitle>
                    {missingParams.map((p) => (
                        <MissingParamRow key={p}>
                            <MissingParamName>{`{{${p}}}`}</MissingParamName>
                            <MissingParamExample>ex: {action.parameterExamples[p]}</MissingParamExample>
                        </MissingParamRow>
                    ))}
                </ValidationError>
            )}
        </ActionRow>
    );
}

export default function TemplateMappingsCard({
    business,
    service,
    isActive,
    modalSegments,
    onNavigate,
}: TemplateMappingsCardProps) {
    const [data, setData] = useState<TemplateMappingsResponse | null>(null);
    const [draft, setDraft] = useState<Map<string, string>>(new Map());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [saving, setSaving] = useState(false);

    const { showToast } = useToast();

    const { isOpen, open, close } = useSingleModalRoute({
        isActive,
        ready: true,
        segments: modalSegments,
        onNavigate,
        modalKey: 'template-mappings',
    });

    const loadData = useCallback(async () => {
        if (!service) return;
        setLoading(true);
        setError(false);
        try {
            const result = await service.getTemplateMappings();
            setData(result);
            setDraft(new Map(result.actions.map((a) => [a.action, a.templateName])));
        } catch (error) {
            console.error('Failed to load template mappings', error);
            setError(true);
            showToast('Não foi possível carregar os templates.', 'error', 'modal');
        } finally {
            setLoading(false);
        }
    }, [service, showToast]);

    useEffect(() => {
        if (isOpen) {
            loadData();
        }
    }, [isOpen, loadData]);

    const hasChanges = data ? data.actions.some((a) => draft.get(a.action) !== a.templateName) : false;

    const handleSelectChange = (action: string, templateName: string) => {
        setDraft((prev) => {
            const next = new Map(prev);
            next.set(action, templateName);
            return next;
        });
    };

    const handleSave = async () => {
        if (!service || !data) return;
        const changed = data.actions.filter((a) => draft.get(a.action) !== a.templateName);
        if (changed.length === 0) return;

        setSaving(true);
        try {
            await service.setTemplateMappings(
                changed.map((action) => ({ action: action.action, templateName: draft.get(action.action)! })),
            );
            await loadData();
            showToast('Templates atualizados!', 'success', 'modal');
        } catch (error: unknown) {
            const message =
                error instanceof Object && 'status' in error && error.status === 400
                    ? 'Parâmetros incompatíveis'
                    : 'Erro ao salvar';
            showToast(message, 'error', 'modal');
        } finally {
            setSaving(false);
        }
    };

    const isWhatsAppProduction = business.whatsapp?.type === 'cloud_api';
    if (!isWhatsAppProduction) return null;

    const activeActions = data?.actions.filter((a) => isActionActive(a.action, business)) ?? [];
    const inactiveActions = data?.actions.filter((a) => !isActionActive(a.action, business)) ?? [];

    return (
        <>
            <ConfigCard onClick={() => open()} style={{ marginTop: '12px' }}>
                <ConfigInfo>
                    <ConfigTitle>Templates de mensagem</ConfigTitle>
                    <ConfigSummary>
                        <SummaryItem>Configure quais templates do WhatsApp são usados em cada ação</SummaryItem>
                    </ConfigSummary>
                </ConfigInfo>
            </ConfigCard>

            <Modal
                isOpen={isOpen}
                onClose={close}
                title="Templates de mensagem"
                footer={
                    <>
                        <Button onClick={close} disabled={saving}>
                            Cancelar
                        </Button>
                        <Button primary onClick={handleSave} disabled={!hasChanges || saving}>
                            {saving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </>
                }
            >
                {loading && !data ? (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#888' }}>Carregando...</div>
                ) : error && !data ? (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#888' }}>
                        Não foi possível carregar os templates.
                        <br />
                        <Button onClick={loadData} style={{ marginTop: '12px' }}>
                            Tentar novamente
                        </Button>
                    </div>
                ) : data ? (
                    <ActionList>
                        {activeActions.map((action) => (
                            <ActionRowContent
                                key={action.action}
                                action={action}
                                draft={draft}
                                availableTemplates={data.availableTemplates}
                                saving={saving}
                                onSelectChange={handleSelectChange}
                            />
                        ))}
                        {inactiveActions.length > 0 && (
                            <InactiveSection>
                                <InactiveSummary>Mais templates</InactiveSummary>
                                <InactiveContent>
                                    {inactiveActions.map((action) => (
                                        <ActionRowContent
                                            key={action.action}
                                            action={action}
                                            draft={draft}
                                            availableTemplates={data.availableTemplates}
                                            saving={saving}
                                            onSelectChange={handleSelectChange}
                                        />
                                    ))}
                                </InactiveContent>
                            </InactiveSection>
                        )}
                        <WarningBanner>
                            Ao criar ou alterar templates no WhatsApp, eles precisam ser aprovados pela Meta antes de
                            funcionar. Esse processo pode levar várias horas.
                        </WarningBanner>
                        {data.managerUrl && (
                            <ExternalLink as="a" href={data.managerUrl} target="_blank" rel="noopener noreferrer">
                                Gerenciar templates no WhatsApp
                            </ExternalLink>
                        )}
                    </ActionList>
                ) : null}
            </Modal>
        </>
    );
}
